import { useCallback, useRef } from 'react';

import type { AssetResource, VideoAssetResource } from '@/hook/use_app_assets';

const NEIGHBOR_PRELOAD_DISTANCE = 3;

export function use_asset_neighbor_preloader(asset_resources: AssetResource[]) {
  const preloaded_source_urls_ref = useRef(new Set<string>());
  const preload_link_elements_ref = useRef(new Map<string, HTMLLinkElement>());
  const pending_preload_images_ref = useRef(new Map<string, HTMLImageElement>());

  return useCallback(
    (asset_index: number) => {
      const neighbor_asset_resources = get_neighbor_asset_resources(
        asset_resources,
        asset_index,
      );
      const preload_source_urls = neighbor_asset_resources.flatMap(
        get_asset_preload_source_urls,
      );

      preload_source_urls.forEach(preload_source_url => {
        preload_image_source_url({
          pending_preload_images: pending_preload_images_ref.current,
          preload_link_elements: preload_link_elements_ref.current,
          preloaded_source_urls: preloaded_source_urls_ref.current,
          source_url: preload_source_url,
        });
      });
    },
    [asset_resources],
  );
}

function get_neighbor_asset_resources(
  asset_resources: AssetResource[],
  asset_index: number,
) {
  const start_asset_index = Math.max(
    asset_index - NEIGHBOR_PRELOAD_DISTANCE,
    0,
  );
  const end_asset_index = Math.min(
    asset_index + NEIGHBOR_PRELOAD_DISTANCE + 1,
    asset_resources.length,
  );

  return asset_resources
    .slice(start_asset_index, end_asset_index)
    .filter((_, neighbor_asset_offset) => {
      const neighbor_asset_index = start_asset_index + neighbor_asset_offset;

      return neighbor_asset_index !== asset_index;
    });
}

function get_asset_preload_source_urls(asset_resource: AssetResource) {
  if (asset_resource.kind === 'image') {
    return [asset_resource.preview_source_url, asset_resource.source_url];
  }

  return get_video_asset_preload_source_urls(asset_resource);
}

function get_video_asset_preload_source_urls(
  asset_resource: VideoAssetResource,
) {
  if (!asset_resource.responsive_variant_source_urls) {
    return [asset_resource.poster_source_url];
  }

  return [
    asset_resource.responsive_variant_source_urls.desktop.poster_source_url,
    asset_resource.responsive_variant_source_urls.mobile.poster_source_url,
  ];
}

function preload_image_source_url({
  pending_preload_images,
  preload_link_elements,
  preloaded_source_urls,
  source_url,
}: {
  pending_preload_images: Map<string, HTMLImageElement>;
  preload_link_elements: Map<string, HTMLLinkElement>;
  preloaded_source_urls: Set<string>;
  source_url: string;
}) {
  if (preloaded_source_urls.has(source_url)) {
    return;
  }

  const preload_image = new Image();

  preloaded_source_urls.add(source_url);
  preload_link_elements.set(
    source_url,
    create_image_preload_link_element(source_url),
  );
  pending_preload_images.set(source_url, preload_image);

  preload_image.decoding = 'async';
  preload_image.fetchPriority = 'high';
  preload_image.onload = () => {
    pending_preload_images.delete(source_url);
  };
  preload_image.onerror = () => {
    const preload_link_element = preload_link_elements.get(source_url);

    preload_link_element?.remove();
    preload_link_elements.delete(source_url);
    preloaded_source_urls.delete(source_url);
    pending_preload_images.delete(source_url);
  };
  preload_image.src = source_url;
}

function create_image_preload_link_element(source_url: string) {
  const preload_link_element = document.createElement('link');

  preload_link_element.rel = 'preload';
  preload_link_element.as = 'image';
  preload_link_element.href = source_url;
  preload_link_element.setAttribute('fetchpriority', 'high');
  document.head.appendChild(preload_link_element);

  return preload_link_element;
}
